import React, { useEffect, useState } from "react";
import useUserStore from "../../../stores/user";
import supabase from "../../../utils/supabase";
import { useT } from "../../../stores/uiLang";
import { TD_T } from "../../../i18n/teacherDashboard";

interface TeacherInformationProps {
  className?: string;
}

const TeacherInformation: React.FC<TeacherInformationProps> = ({
  className = "",
}) => {
  const t = useT(TD_T);
  const user = useUserStore((state) => state.user);
  const profile = useUserStore((state) => state.profile);
  const [teacherDistrict, setTeacherDistrict] = useState<any>(null);
  const [teacherSchool, setTeacherSchool] = useState<any>(null);
  const [teacherClasses, setTeacherClasses] = useState<any[]>([]);
  const [selectedClass, setSelectedClass] = useState<string>("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !profile) return;

    async function loadTeacherData() {
      await Promise.all([
        loadTeacherDistrict(),
        loadTeacherSchool(),
        loadTeacherClasses(),
      ]);
      setLoading(false);
    }

    loadTeacherData();
  }, [user?.id, profile]);

  async function loadTeacherDistrict() {
    if (!profile.district) {
      setTeacherDistrict(null);
      return;
    }

    const { data, error } = await supabase
      .from("districts")
      .select("*")
      .eq("id", profile.district)
      .maybeSingle();

    if (error) {
      console.error("Error loading teacher district:", error);
      setTeacherDistrict(null);
    } else {
      setTeacherDistrict(data);
    }
  }

  async function loadTeacherSchool() {
    if (!profile.school) {
      setTeacherSchool(null);
      return;
    }

    const { data, error } = await supabase
      .from("schools")
      .select("*")
      .eq("id", profile.school)
      .maybeSingle();

    if (error) {
      console.error("Error loading teacher school:", error);
      setTeacherSchool(null);
    } else {
      setTeacherSchool(data);
    }
  }

  async function loadTeacherClasses() {
    if (!user?.id) {
      setTeacherClasses([]);
      return;
    }

    const { data, error } = await supabase
      .from("classes")
      .select("*")
      .eq("user_id", user.id)
      .order("name");

    if (error) {
      console.error("Error loading teacher classes:", error);
      setTeacherClasses([]);
      setSelectedClass("");
    } else {
      const classes = data || [];
      setTeacherClasses(classes);
      // Set the first class as selected if classes exist
      if (classes.length > 0) {
        setSelectedClass(classes[0].id);
      } else {
        setSelectedClass("");
      }
    }
  }

  if (loading) {
    return (
      <div className={`bg-white shadow rounded-lg p-6 ${className}`}>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center space-x-3">
                <div className="h-5 w-5 bg-gray-200 rounded"></div>
                <div className="flex-1">
                  <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white shadow rounded-lg p-6 ${className}`}>
      <h2 className="text-lg font-medium text-gray-900 mb-4">{t.myInfo}</h2>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">{t.school}</p>
            <p className="text-sm text-gray-900">
              {teacherSchool ? teacherSchool.name : t.noSchool}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">{t.district}</p>
            <p className="text-sm text-gray-900">
              {teacherDistrict ? teacherDistrict.name : t.noDistrict}
            </p>
          </div>
        </div>

        {/* Run E2 J2: "State" (PFL state standards, Oklahoma fallback) hidden for Project Explore. */}

        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-500">{t.myClasses}</p>
            {teacherClasses.length > 0 ? (
              <select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className="mt-1 block w-auto pl-3 pr-10 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                {teacherClasses.map((classItem) => (
                  <option key={classItem.id} value={classItem.id}>
                    {classItem.name}
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-sm text-gray-500 mt-1">{t.noClasses}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherInformation;
